import {tx, ptx, usePageText} from '../lib/page-text';
import { useProgramPermissions } from './program/ProducerPanel';
import { BoardPages } from './program/BoardPages';
import { useEffect, useId, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { useParams, useSearchParams } from "../lib/localized-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api-client";
import { useAuth } from "../lib/auth-store";
import { DashboardDialog } from "./DashboardDialog";
import { BoardColorInput } from "./board/BoardColorInput";
import { BoardIcon } from "./board/BoardIcon";
import { BoardShapeView } from "./board/BoardShape";
import {
  bounds,
  translated,
  resized,
  boardLimit,
  COLORS,
  type Board,
  type Point,
  type Shape,
  type Tool,
} from "./board/model";
import { exportCanvas, uploadBoardImage, useBoardImages } from "./board/media";
const message = (e: any) =>
  e?.response?.data?.message ?? e?.message ?? "The board could not be saved.";
const labels: Record<string, string> = {
  select: "Select",
  hand: "Pan",
  pen: "Pen",
  highlight: "Highlighter",
  erase: "Eraser",
  rect: "Rectangle",
  ellipse: "Ellipse",
  line: "Line",
  arrow: "Arrow",
  triangle: "Triangle",
  diamond: "Diamond",
  text: "Text box",
  note: "Sticky note",
  image: "Image",
};
type Snapshot = Pick<Board, "shapes" | "background">;
type Gesture = {
  latest?: Shape;
  type: "draw" | "move" | "resize" | "pan";
  start: Point;
  shape?: Shape;
  client: Point;
  scroll: Point;
};

export function InteractiveBoard({
  streamId,
  viewerKey,
  token,
  presentation = false,
  onCanvasChange,
}: {
  streamId?: string;
  viewerKey?: string;
  token?: string;
  presentation?: boolean;
  onCanvasChange?: (svg: SVGSVGElement | null) => void;
}) {
  usePageText();
  const permissions = useProgramPermissions(streamId);
  const role = useAuth((s) => s.user?.role),
    canEdit = !!streamId && (["OWNER", "ADMIN", "MEMBER"].includes(role ?? "") || !!permissions.data?.permissions.board);
  const endpoint = streamId
    ? `/me/streams/${streamId}/board`
    : `/streams/${encodeURIComponent(viewerKey ?? "")}/board`;
  const query = useQuery({
    queryKey: ["board", streamId ?? viewerKey, token],
    queryFn: async () =>
      (
        await api.get<Board>(endpoint, {
          params: token ? { t: token } : undefined,
        })
      ).data,
    refetchInterval: 2000,
    retry: false,
  });
  const [board, setBoard] = useState<Board>({ version: 0, shapes: [] }),
    [tool, setTool] = useState<Tool>("select"),
    [color, setColor] = useState("#172554"),
    [fill, setFill] = useState("none"),
    [width, setWidth] = useState(3);
  const [selected, setSelected] = useState<string | null>(null),
    [preview, setPreview] = useState<Shape | null>(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState(""),
    [conflict, setConflict] = useState(false);
  const [unsaved, setUnsaved] = useState<Snapshot | null>(null),
    [undo, setUndo] = useState<Snapshot[]>([]),
    [redo, setRedo] = useState<Snapshot[]>([]),
    [confirmClear, setConfirmClear] = useState(false);
  const [textEditor, setTextEditor] = useState<Shape | null>(null),
    [uploading, setUploading] = useState(false),
    [exporting, setExporting] = useState(false),
    [zoom, setZoom] = useState(100),
    [layers, setLayers] = useState(false),
    [dragging, setDragging] = useState(false);
  const root = useRef<HTMLElement>(null),
    svg = useRef<SVGSVGElement>(null),
    viewport = useRef<HTMLDivElement>(null),
    file = useRef<HTMLInputElement>(null),
    gesture = useRef<Gesture | null>(null),
    busy = useRef(false),
    uploadBusy = useRef(false),
    retryAction = useRef<"draw" | "undo" | "redo">("draw");
  const current = useRef(board);
  current.current = board;
  const pattern = "board-" + useId().replace(/[^a-zA-Z0-9]/g, "");
  const base = unsaved ?? board,
    shapes = preview
      ? base.shapes.some((s) => s.id === preview.id)
        ? base.shapes.map((s) => (s.id === preview.id ? preview : s))
        : [...base.shapes, preview]
      : base.shapes;
  const media = useBoardImages(shapes, endpoint, token),
    selection = shapes.find((s) => s.id === selected),
    locked =
      !canEdit ||
      pending ||
      uploading ||
      !!unsaved ||
      query.isError ||
      query.isPending;
  // Optional host-only video composition. Never opens a public board route.
  useEffect(() => {
    if (!onCanvasChange) return;
    const ready = Date.now() - query.dataUpdatedAt < 10000 && !query.isPending && !query.isError && !media.failed &&
      shapes.every(shape => !shape.imageId || !!media.images[shape.imageId]);
    onCanvasChange(ready ? svg.current : null);
    const timeout = window.setTimeout(() => onCanvasChange(null), Math.max(0, query.dataUpdatedAt + 10000 - Date.now()));
    return () => clearTimeout(timeout);
  }, [onCanvasChange, shapes, base.background, media.images, media.failed, query.isPending, query.isError, query.dataUpdatedAt]);
  useEffect(() => () => onCanvasChange?.(null), [onCanvasChange]);
  useEffect(() => {
    if (
      !query.data ||
      busy.current ||
      unsaved ||
      gesture.current ||
      textEditor ||
      uploadBusy.current ||
      query.data.version < current.current.version
    )
      return;
    if (query.data.version !== current.current.version) {
      setUndo([]);
      setRedo([]);
    }
    setBoard(query.data);
  }, [query.data, unsaved, pending, uploading, textEditor]);
  useEffect(() => {
    if (!presentation || window.parent === window) return;
    window.parent.postMessage(
      {
        type: "kardolive-board-ready",
        ready: !query.isPending && !query.isError,
      },
      window.location.origin,
    );
  }, [presentation, query.isPending, query.isError]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (busy.current || unsaved || uploadBusy.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);
  async function save(
    next: Snapshot,
    action: "draw" | "undo" | "redo" = "draw",
  ) {
    if (!canEdit || busy.current || conflict) return;
    const limit = boardLimit(next.shapes);
    if (limit) {
      setError(limit);
      return;
    }
    busy.current = true;
    setPending(true);
    setError("");
    setUnsaved(next);
    retryAction.current = action;
    const previous = {
      shapes: current.current.shapes,
      background: current.current.background,
    };
    try {
      const saved = (
        await api.patch<Board>(endpoint, {
          version: current.current.version,
          ...next,
        })
      ).data;
      if (action === "undo") {
        setUndo((x) => x.slice(0, -1));
        setRedo((x) => [...x.slice(-19), previous]);
      } else if (action === "redo") {
        setRedo((x) => x.slice(0, -1));
        setUndo((x) => [...x.slice(-19), previous]);
      } else {
        setUndo((x) => [...x.slice(-19), previous]);
        setRedo([]);
      }
      current.current = saved;
      setBoard(saved);
      setUnsaved(null);
      await query.refetch();
    } catch (e: any) {
      setError(message(e));
      setConflict(e?.response?.status === 409);
    } finally {
      busy.current = false;
      setPending(false);
    }
  }
  function commit(next: Shape[]) {
    void save({ shapes: next, background: current.current.background });
  }
  function updateSelected(patch: Partial<Shape>) {
    if (locked || !selection) return;
    commit(
      board.shapes.map((s) => (s.id === selection.id ? { ...s, ...patch } : s)),
    );
  }
  function choose(t: Tool) {
    setTool(t);
    setSelected(null);
    root.current?.focus({ preventScroll: true });
  }
  function point(e: PointerEvent<SVGSVGElement>): Point {
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(
      e.currentTarget.getScreenCTM()!.inverse(),
    );
    return [Math.max(0, Math.min(1000, p.x)), Math.max(0, Math.min(600, p.y))];
  }
  function begin(e: PointerEvent<SVGSVGElement>) {
    if (locked || e.button !== 0) return;
    e.preventDefault();
    root.current?.focus({ preventScroll: true });
    const p = point(e),
      target = e.target as Element,
      id = target.closest("[data-shape]")?.getAttribute("data-shape"),
      hit = board.shapes.find((s) => s.id === id);
    if (tool === "erase") {
      if (hit) commit(board.shapes.filter((s) => s.id !== hit.id));
      return;
    }
    let g: Gesture = {
      type: "draw",
      start: p,
      client: [e.clientX, e.clientY],
      scroll: [
        viewport.current?.scrollLeft ?? 0,
        viewport.current?.scrollTop ?? 0,
      ],
    };
    if (tool === "hand") g.type = "pan";
    else if (target.closest("[data-resize]") && selection) {
      g.type = "resize";
      g.shape = selection;
    } else if (tool === "select") {
      setSelected(hit?.id ?? null);
      if (!hit) return;
      g.type = "move";
      g.shape = hit;
    } else {
      setSelected(null);
      const free = tool === "pen" || tool === "highlight";
      g.shape = {
        id: crypto.randomUUID(),
        kind: free ? "pen" : (tool as Shape["kind"]),
        color: tool === "highlight" && color === "#172554" ? "#eab308" : color,
        width: tool === "highlight" ? Math.max(10, width) : width,
        points: free ? [p] : [p, p],
        fill,
        opacity: tool === "highlight" ? 0.3 : 1,
      };
    }
    gesture.current = g;
    if (g.shape) setPreview(g.shape);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: PointerEvent<SVGSVGElement>) {
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pan") {
      if (viewport.current) {
        viewport.current.scrollLeft = g.scroll[0] - (e.clientX - g.client[0]);
        viewport.current.scrollTop = g.scroll[1] - (e.clientY - g.client[1]);
      }
      return;
    }
    if (!g.shape) return;
    const p = point(e);
    if (g.type === "move") {
      g.latest = translated(g.shape, p[0] - g.start[0], p[1] - g.start[1]);
      setPreview(g.latest);
    } else if (g.type === "resize") {
      const b = bounds(g.shape);
      g.latest = resized(
        g.shape,
        [b.x + b.w + p[0] - g.start[0], b.y + b.h + p[1] - g.start[1]],
        g.shape.kind === "image" || e.shiftKey,
      );
      setPreview(g.latest);
    } else if (g.shape.kind === "pen") {
      if (g.shape.points.length >= 600) return;
      const last = g.shape.points.at(-1)!;
      if (Math.hypot(p[0] - last[0], p[1] - last[1]) < 1) return;
      g.shape = { ...g.shape, points: [...g.shape.points, p] };
      setPreview(g.shape);
    } else {
      g.shape = { ...g.shape, points: [g.start, p] };
      setPreview(g.shape);
    }
  }
  function end() {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    const shape = g.latest ?? g.shape;
    setPreview(null);
    if (g.type === "pan" || !shape) return;
    if (g.type === "draw") {
      if (
        shape.kind !== "pen" &&
        Math.hypot(
          shape.points[1][0] - shape.points[0][0],
          shape.points[1][1] - shape.points[0][1],
        ) < 4
      )
        return;
      if (
        !["pen", "line", "arrow"].includes(shape.kind) &&
        (bounds(shape).w < 2 || bounds(shape).h < 2)
      )
        return;
      commit([...current.current.shapes, shape]);
    } else if (JSON.stringify(shape) !== JSON.stringify(g.shape))
      commit(
        current.current.shapes.map((s) => (s.id === shape.id ? shape : s)),
      );
  }
  function newText(kind: "text" | "note") {
    if (locked) return;
    setTextEditor({
      id: crypto.randomUUID(),
      kind,
      color,
      width: 1,
      points: [
        [340, 200],
        [660, kind === "note" ? 400 : 340],
      ],
      text: "",
      fill: kind === "note" ? "#fef3c7" : "none",
      fontSize: 24,
      font: "sans-serif",
      align: "left",
    });
  }
  function duplicate() {
    if (!selection || locked) return;
    const copy = { ...translated(selection, 20, 20), id: crypto.randomUUID() };
    setSelected(copy.id);
    commit([...board.shapes, copy]);
  }
  function remove() {
    if (!selection || locked) return;
    setSelected(null);
    commit(board.shapes.filter((s) => s.id !== selection.id));
  }
  async function addImage(files: File[]) {
    if (locked || uploadBusy.current || !files.length) return;
    if (files.length !== 1) {
      setError("Add one image at a time.");
      return;
    }
    if (board.shapes.filter((s) => s.kind === "image").length >= 20) {
      setError("Remove an image before adding another (20 maximum).");
      return;
    }
    uploadBusy.current = true;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadBoardImage(files[0], endpoint),
        scale = Math.min(500 / uploaded.width, 360 / uploaded.height, 1),
        w = uploaded.width * scale,
        h = uploaded.height * scale,
        s: Shape = {
          id: crypto.randomUUID(),
          kind: "image",
          color: "#172554",
          width: 1,
          points: [
            [(1000 - w) / 2, (600 - h) / 2],
            [(1000 + w) / 2, (600 + h) / 2],
          ],
          imageId: uploaded.imageId,
        };
      setTool("select");
      setSelected(s.id);
      await save({
        shapes: [...current.current.shapes, s],
        background: current.current.background,
      });
    } catch (e) {
      setError(message(e));
    } finally {
      uploadBusy.current = false;
      setUploading(false);
      if (file.current) file.current.value = "";
    }
  }
  async function exportBoard(format: "svg" | "png") {
    if (!svg.current) return;
    if (shapes.some((s) => s.imageId && !media.images[s.imageId])) {
      setError("Some images have not loaded. Retry images before exporting.");
      return;
    }
    setExporting(true);
    try {
      await exportCanvas(svg.current, format);
    } catch (e) {
      setError(message(e));
    } finally {
      setExporting(false);
    }
  }
  async function reload() {
    setUnsaved(null);
    setError("");
    setConflict(false);
    setUndo([]);
    setRedo([]);
    setSelected(null);
    const result = await query.refetch();
    if (result.data) {
      current.current = result.data;
      setBoard(result.data);
    }
  }
  const swatch = selection?.color ?? color,
    currentFill = selection?.fill ?? fill,
    textSelected =
      selection && (selection.kind === "text" || selection.kind === "note");
  const toolButton = (t: Tool) => (
    <button
      type="button"
      key={t}
      className={`wb-tool ${tool === t ? "active" : ""}`}
      title={tx(labels[t])}
      aria-label={tx(labels[t])}
      aria-pressed={tool === t}
      disabled={locked}
      onClick={() => choose(t)}
    >
      <BoardIcon name={t} />
    </button>
  );
  return (
    <section
      ref={root}
      tabIndex={-1}
      className={`card interactive-board wb-editor${presentation ? " wb-presentation" : ""}`}
      aria-label={tx("Interactive board")}
      onKeyDown={(e) => {
        if (
          locked ||
          (e.target as Element).closest(
            "input,textarea,select,[contenteditable],dialog",
          )
        )
          return;
        const mod = e.ctrlKey || e.metaKey,
          k = e.key.toLowerCase();
        if (mod && k === "z") {
          e.preventDefault();
          const list = e.shiftKey ? redo : undo;
          if (list.length)
            void save(list.at(-1)!, e.shiftKey ? "redo" : "undo");
        } else if (mod && k === "d") {
          e.preventDefault();
          duplicate();
        } else if (k === "delete" || k === "backspace") {
          e.preventDefault();
          remove();
        } else if (e.key === "Escape") {
          setSelected(null);
          setTool("select");
        } else if (
          selection &&
          ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
        ) {
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1,
            s = translated(
              selection,
              e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0,
              e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0,
            );
          commit(board.shapes.map((x) => (x.id === s.id ? s : x)));
        } else if (!(e.target as Element).closest("button,summary,a") && !mod) {
          const shortcut: Record<string, Tool> = {
            v: "select",
            h: "hand",
            p: "pen",
            e: "erase",
            r: "rect",
            o: "ellipse",
            a: "arrow",
          };
          if (shortcut[k]) choose(shortcut[k]);
          else if (k === "t") newText("text");
          else if (k === "n") newText("note");
        }
      }}
      onPaste={(e) => {
        if ((e.target as Element).closest("input,textarea,[contenteditable]"))
          return;
        const images = Array.from(e.clipboardData.files).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (images.length) {
          e.preventDefault();
          void addImage(images);
        }
      }}
    >
      {streamId && !presentation && <BoardPages streamId={streamId} version={board.version} disabled={locked} onChange={() => query.refetch()} />}
      <header className="wb-header">
        <div>
          <div className="wb-eyebrow">{tx("SHARED CANVAS")}</div>
          <h3>{tx("Interactive board")}</h3>
        </div>
        <div className="wb-header-actions">
          <span
            className={`wb-status ${unsaved || error ? "warning" : ""}`}
            role="status"
          >
            {query.isPending
              ? tx("Loading board…")
              : uploading
                ? tx("Uploading image…")
                : pending
                  ? tx("Saving…")
                  : unsaved
                    ? tx("Unsaved changes")
                    : ptx("Saved · revision {v0}", {v0: (board.version)})}
          </span>
          {canEdit && (
            <>
              <button
                type="button"
                className="wb-tool"
                aria-label={tx("Undo")}
                title={tx("Undo (⌘/Ctrl Z)")}
                disabled={locked || !undo.length}
                onClick={() => void save(undo.at(-1)!, "undo")}
              >
                <BoardIcon name="undo" />
              </button>
              <button
                type="button"
                className="wb-tool"
                aria-label={tx("Redo")}
                title={tx("Redo (⌘/Ctrl Shift Z)")}
                disabled={locked || !redo.length}
                onClick={() => void save(redo.at(-1)!, "redo")}
              >
                <BoardIcon name="redo" />
              </button>
            </>
          )}
          <details className="wb-menu">
            <summary className="btn outline sm">{ptx("{v0} Export", {v0: (<BoardIcon name="export" />)})}</summary>
            <div className="wb-menu-panel">
              <button
                type="button"
                disabled={exporting || query.isPending}
                onClick={() => void exportBoard("png")}
              >{tx("Download PNG")}</button>
              <button
                type="button"
                disabled={exporting || query.isPending}
                onClick={() => void exportBoard("svg")}
              >{tx("Export SVG")}</button>
            </div>
          </details>
        </div>
      </header>
      {canEdit && (
        <>
          <div className="wb-toolbar" role="toolbar" aria-label={tx("Drawing tools")}>
            <div className="wb-tool-group">
              {(["select", "hand"] as Tool[]).map(toolButton)}
            </div>
            <div className="wb-tool-group">
              {(["pen", "highlight", "erase"] as Tool[]).map(toolButton)}
            </div>
            <details className="wb-menu">
              <summary className="wb-tool wb-shapes" aria-label={tx("Shapes")}>
                <BoardIcon
                  name={
                    [
                      "rect",
                      "ellipse",
                      "line",
                      "arrow",
                      "triangle",
                      "diamond",
                    ].includes(tool)
                      ? tool
                      : "rect"
                  }
                />
                <span>{tx("Shapes")}</span>
                <span aria-hidden="true">⌄</span>
              </summary>
              <div className="wb-menu-panel wb-shape-menu">
                {(
                  [
                    "rect",
                    "ellipse",
                    "line",
                    "arrow",
                    "triangle",
                    "diamond",
                  ] as Tool[]
                ).map((t) => (
                  <button
                    type="button"
                    key={t}
                    disabled={locked}
                    onClick={(e) => {
                      choose(t);
                      e.currentTarget
                        .closest("details")
                        ?.removeAttribute("open");
                    }}
                  >
                    <BoardIcon name={t} />
                    {tx(labels[t])}
                  </button>
                ))}
              </div>
            </details>
            <div className="wb-tool-group">
              <button
                type="button"
                className="wb-tool"
                title={tx("Text box (T)")}
                aria-label={tx("Text box")}
                disabled={locked}
                onClick={() => newText("text")}
              >
                <BoardIcon name="text" />
              </button>
              <button
                type="button"
                className="wb-tool"
                title={tx("Sticky note (N)")}
                aria-label={tx("Sticky note")}
                disabled={locked}
                onClick={() => newText("note")}
              >
                <BoardIcon name="note" />
              </button>
              <button
                type="button"
                className="wb-tool"
                title={tx("Add image · JPEG, PNG or WebP")}
                aria-label={tx("Add image")}
                disabled={locked}
                onClick={() => file.current?.click()}
              >
                <BoardIcon name="image" />
              </button>
            </div>
            <input
              ref={file}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => void addImage(Array.from(e.target.files ?? []))}
            />
            <span className="wb-tool-hint">
              {tool === "select"
                ? tx("Select, drag & resize")
                : tool === "hand"
                  ? tx("Drag to pan at higher zoom")
                  : tool === "erase"
                    ? tx("Tap an object to erase it")
                    : ptx("Drag to draw · {v0}", {v0: (tx(labels[tool]))})}
            </span>
          </div>
          <div className="wb-properties">
            <div className="wb-palette" role="group" aria-label={tx("Color palette")}>
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={`wb-swatch ${swatch === c ? "active" : ""}`}
                  style={{ background: c }}
                  aria-label={tx("Color {v0}", {v0: (c)})}
                  aria-pressed={swatch === c}
                  disabled={locked}
                  onClick={() => {
                    setColor(c);
                    updateSelected({ color: c });
                  }}
                />
              ))}
              <label className="wb-custom-color" title={tx("Custom color")}>
                <span className="sr-only">{tx("Custom color")}</span>
                <BoardColorInput
                  label={tx("Custom color")}
                  value={swatch}
                  disabled={locked}
                  onCommit={(value) => {
                    setColor(value);
                    updateSelected({ color: value });
                  }}
                />
              </label>
            </div>
            <label className="wb-property">{ptx("Stroke{v0}", {v0: (<select
                value={selection?.width ?? width}
                disabled={locked}
                onChange={(e) => {
                  setWidth(+e.target.value);
                  updateSelected({ width: +e.target.value });
                }}
              >
                {[1, 2, 3, 4, 6, 10, 12, 16].map((n) => (
                  <option key={n} value={n}>{ptx("{v0} px", {v0: (n)})}</option>
                ))}
              </select>)})}</label>
            <div className="wb-property">{tx("Fill")}<button
                type="button"
                className="wb-no-fill"
                aria-label={tx("No fill")}
                aria-pressed={currentFill === "none"}
                disabled={locked}
                onClick={() => {
                  setFill("none");
                  updateSelected({ fill: "none" });
                }}
              >
                ∅
              </button>
              <BoardColorInput
                label={tx("Fill color")}
                value={currentFill === "none" ? "#dbeafe" : currentFill}
                disabled={locked}
                onCommit={(value) => {
                  setFill(value);
                  updateSelected({ fill: value });
                }}
              />
            </div>
            {selection && (
              <div className="wb-selection-actions">
                {textSelected && (
                  <button
                    type="button"
                    className="btn outline sm"
                    disabled={locked}
                    onClick={() => setTextEditor(selection)}
                  >{tx("Edit text")}</button>
                )}
                {[
                  ["duplicate", "Duplicate", duplicate],
                  [
                    "front",
                    "Bring to front",
                    () =>
                      commit([
                        ...board.shapes.filter((s) => s.id !== selection.id),
                        selection,
                      ]),
                  ],
                  [
                    "back",
                    "Send to back",
                    () =>
                      commit([
                        selection,
                        ...board.shapes.filter((s) => s.id !== selection.id),
                      ]),
                  ],
                  ["delete", "Delete selected", remove],
                ].map(([icon, label, action]) => (
                  <button
                    type="button"
                    className="wb-tool"
                    key={String(icon)}
                    title={String(label)}
                    aria-label={String(label)}
                    disabled={locked}
                    onClick={action as () => void}
                  >
                    <BoardIcon name={String(icon)} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {(query.isError || error) && (
        <div className="board-notice" role="alert">
          {error || message(query.error)}{" "}
          {unsaved && (
            <span>{tx("Your unsaved changes are kept here. Export a copy before loading another version.")}</span>
          )}{" "}
          {unsaved && !conflict && (
            <button
              type="button"
              className="btn outline sm"
              disabled={pending}
              onClick={() => void save(unsaved, retryAction.current)}
            >{tx("Retry save")}</button>
          )}{" "}
          <button
            type="button"
            className="btn outline sm"
            disabled={pending}
            onClick={() => void reload()}
          >
            {unsaved ? tx("Discard changes and reload") : tx("Retry")}
          </button>
        </div>
      )}
      {media.failed && (
        <div className="board-notice" role="alert">{tx("Some board images could not load.")}{" "}
          <button
            type="button"
            className="btn outline sm"
            onClick={media.retry}
          >{tx("Retry images")}</button>
        </div>
      )}
      <div className={`wb-workspace ${canEdit && layers ? "with-layers" : ""}`}>
        <div
          ref={viewport}
          className={`wb-viewport ${dragging ? "is-dragging" : ""}`}
          onDragOver={(e) => {
            if (canEdit && e.dataTransfer.types.includes("Files")) {
              e.preventDefault();
              setDragging(true);
            }
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node))
              setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void addImage(Array.from(e.dataTransfer.files));
          }}
        >
          {!query.isPending && (!query.isError || (canEdit && unsaved)) && (
            <svg
              ref={svg}
              className="board-canvas"
              style={{
                touchAction: canEdit ? "none" : "pan-y",
                width: canEdit ? `${zoom}%` : "100%",
                cursor:
                  tool === "hand"
                    ? "grab"
                    : tool === "select"
                      ? "default"
                      : "crosshair",
              }}
              viewBox="0 0 1000 600"
              role="img"
              aria-label={tx("Shared drawing canvas")}
              onPointerDown={begin}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={() => {
                gesture.current = null;
                setPreview(null);
              }}
              onDoubleClick={(e) => {
                if (locked) return;
                const id = (e.target as Element)
                    .closest("[data-shape]")
                    ?.getAttribute("data-shape"),
                  s = board.shapes.find((s) => s.id === id);
                if (s && (s.kind === "text" || s.kind === "note"))
                  setTextEditor(s);
              }}
            >
              <defs>
                <pattern
                  id={pattern}
                  width="24"
                  height="24"
                  patternUnits="userSpaceOnUse"
                >
                  {base.background === "grid" ? (
                    <path
                      d="M24 0H0V24"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="1"
                    />
                  ) : (
                    <circle cx="2" cy="2" r="1" fill="#cbd5e1" />
                  )}
                </pattern>
              </defs>
              <rect width="1000" height="600" fill="#fff" />
              {base.background && base.background !== "plain" && (
                <rect width="1000" height="600" fill={`url(#${pattern})`} />
              )}
              {shapes.map((s) => (
                <BoardShapeView
                  key={s.id}
                  shape={s}
                  image={s.imageId ? media.images[s.imageId] : undefined}
                  prefix={pattern}
                />
              ))}
              {canEdit &&
                selection &&
                !locked &&
                (() => {
                  const b = bounds(selection);
                  return (
                    <g data-editor="selection">
                      <rect
                        x={b.x - 3}
                        y={b.y - 3}
                        width={b.w + 6}
                        height={b.h + 6}
                        fill="none"
                        stroke="#0284c7"
                        strokeWidth="1.5"
                        strokeDasharray="5 3"
                        pointerEvents="none"
                      />
                      <rect
                        data-resize="true"
                        x={b.x + b.w - 24}
                        y={b.y + b.h - 24}
                        width="24"
                        height="24"
                        rx="3"
                        fill="#fff"
                        stroke="#0284c7"
                        strokeWidth="2"
                        style={{ cursor: "nwse-resize" }}
                      />
                    </g>
                  );
                })()}
            </svg>
          )}
          {query.isPending && (
            <div className="wb-loading" role="status">{tx("Opening shared canvas…")}</div>
          )}
          {canEdit && !shapes.length && !query.isPending && !query.isError && (
            <div className="wb-empty" aria-hidden="true">
              <BoardIcon name="pen" />
              <strong>{tx("A space for your ideas")}</strong>
              <span>{tx("Draw, add text, or drop an image onto the board.")}</span>
            </div>
          )}
          {dragging && (
            <div className="wb-drop-overlay">{tx("Drop one image to add it")}</div>
          )}
        </div>
        {canEdit && layers && (
          <aside className="wb-layers" aria-label={tx("Board layers")}>
            <div className="row-between">
              <strong>{ptx("Objects ({v0})", {v0: (shapes.length)})}</strong>
              <button
                type="button"
                className="wb-tool"
                aria-label={tx("Close layers")}
                onClick={() => setLayers(false)}
              >
                <BoardIcon name="close" />
              </button>
            </div>
            {!shapes.length && (
              <p className="muted">{tx("Your objects will appear here.")}</p>
            )}
            {[...shapes].reverse().map((s, i) => (
              <button
                type="button"
                key={s.id}
                className={selected === s.id ? "active" : ""}
                disabled={locked}
                onClick={() => {
                  setTool("select");
                  setSelected(s.id);
                }}
              >
                <BoardIcon name={s.kind} />
                <span>
                  {s.text?.slice(0, 32) || tx(labels[s.kind] || "Drawing")}
                </span>
                <small>{shapes.length - i}</small>
              </button>
            ))}
          </aside>
        )}
      </div>
      <footer className="wb-footer">
        <span className="wb-follow">
          {canEdit
            ? tx(onCanvasChange ? "Live preview includes current edits" : "Viewers follow saved changes")
            : tx("Shared by your presenters")}
        </span>
        {canEdit && (
          <>
            <label className="wb-property">{ptx("Canvas{v0}", {v0: (<select
                aria-label={tx("Canvas background")}
                disabled={locked}
                value={base.background ?? "plain"}
                onChange={(e) =>
                  void save({
                    shapes: board.shapes,
                    background: e.target.value as Board["background"],
                  })
                }
              >
                <option value="plain">{tx("Plain")}</option>
                <option value="dots">{tx("Dots")}</option>
                <option value="grid">{tx("Grid")}</option>
              </select>)})}</label>
            <div className="wb-zoom">
              <button
                type="button"
                className="wb-tool"
                aria-label={tx("Zoom out")}
                disabled={zoom <= 50}
                onClick={() => setZoom((x) => Math.max(50, x - 25))}
              >
                <BoardIcon name="minus" />
              </button>
              <button
                type="button"
                className="wb-zoom-value"
                title={tx("Fit canvas")}
                aria-label={tx("Fit canvas")}
                onClick={() => {
                  setZoom(100);
                  viewport.current?.scrollTo(0, 0);
                }}
              >
                {zoom}%
              </button>
              <button
                type="button"
                className="wb-tool"
                aria-label={tx("Zoom in")}
                disabled={zoom >= 200}
                onClick={() => setZoom((x) => Math.min(200, x + 25))}
              >
                <BoardIcon name="plus" />
              </button>
            </div>
            <button
              type="button"
              className={`wb-tool ${layers ? "active" : ""}`}
              aria-label={tx("Layers")}
              aria-pressed={layers}
              title={tx("Layers")}
              onClick={() => setLayers((x) => !x)}
            >
              <BoardIcon name="layers" />
            </button>
            <button
              type="button"
              className="wb-tool"
              aria-label={tx("Fullscreen board")}
              title={tx("Fullscreen board")}
              onClick={() => {
                const target =
                  root.current?.closest(".presenter-board") ?? root.current;
                if (document.fullscreenElement)
                  void document
                    .exitFullscreen()
                    .catch(() => setError("Fullscreen could not close."));
                else if (target?.requestFullscreen)
                  void target
                    .requestFullscreen()
                    .catch(() =>
                      setError("Fullscreen is unavailable in this view."),
                    );
                else setError("Fullscreen is unavailable in this browser.");
              }}
            >
              <BoardIcon name="fullscreen" />
            </button>
            <button
              type="button"
              className="btn ghost sm"
              disabled={locked || !board.shapes.length}
              onClick={() => setConfirmClear(true)}
            >{tx("Clear board")}</button>
          </>
        )}
      </footer>
      {confirmClear && (
        <DashboardDialog
          title={tx("Clear the shared board?")}
          onClose={() => setConfirmClear(false)}
        >
          <p>{tx("This removes the objects for everyone watching. You can undo this action while the board stays open.")}</p>
          <div className="actions">
            <button
              type="button"
              className="btn outline"
              onClick={() => setConfirmClear(false)}
            >{tx("Cancel")}</button>
            <button
              type="button"
              className="btn danger"
              disabled={locked}
              onClick={() => {
                setConfirmClear(false);
                setSelected(null);
                commit([]);
              }}
            >{tx("Clear for everyone")}</button>
          </div>
        </DashboardDialog>
      )}
      {textEditor && (
        <DashboardDialog
          title={textEditor.kind === "note" ? tx("Sticky note") : tx("Text box")}
          onClose={() => setTextEditor(null)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (locked || !textEditor.text?.trim()) return;
              const next = { ...textEditor, text: textEditor.text.trim() };
              setSelected(next.id);
              setTool("select");
              commit(
                board.shapes.some((s) => s.id === next.id)
                  ? board.shapes.map((s) => (s.id === next.id ? next : s))
                  : [...board.shapes, next],
              );
              setTextEditor(null);
            }}
          >
            <label className="label">{ptx("Text{v0}", {v0: (<textarea
                aria-label={tx("Text")}
                autoFocus
                className="input"
                rows={5}
                maxLength={2000}
                value={textEditor.text}
                onChange={(e) =>
                  setTextEditor({ ...textEditor, text: e.target.value })
                }
              />)})}</label>
            <div className="wb-text-settings">
              <label>{ptx("Size{v0}", {v0: (<select
                  className="input"
                  value={textEditor.fontSize ?? 24}
                  onChange={(e) =>
                    setTextEditor({ ...textEditor, fontSize: +e.target.value })
                  }
                >
                  {[12, 16, 20, 24, 32, 40, 48, 64, 80].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>)})}</label>
              <label>{ptx("Font{v0}", {v0: (<select
                  className="input"
                  value={textEditor.font ?? "sans-serif"}
                  onChange={(e) =>
                    setTextEditor({
                      ...textEditor,
                      font: e.target.value as Shape["font"],
                    })
                  }
                >
                  <option value="sans-serif">{tx("Sans serif")}</option>
                  <option value="serif">{tx("Serif")}</option>
                  <option value="monospace">{tx("Monospace")}</option>
                </select>)})}</label>
              <label>{ptx("Align{v0}", {v0: (<select
                  className="input"
                  value={textEditor.align ?? "left"}
                  onChange={(e) =>
                    setTextEditor({
                      ...textEditor,
                      align: e.target.value as Shape["align"],
                    })
                  }
                >
                  <option value="left">{tx("Left")}</option>
                  <option value="center">{tx("Center")}</option>
                  <option value="right">{tx("Right")}</option>
                </select>)})}</label>
              <button
                type="button"
                className={`btn outline ${textEditor.bold ? "active" : ""}`}
                aria-pressed={!!textEditor.bold}
                onClick={() =>
                  setTextEditor({ ...textEditor, bold: !textEditor.bold })
                }
              >
                <b>{tx("Bold")}</b>
              </button>
              <button
                type="button"
                className="btn outline"
                aria-pressed={!!textEditor.italic}
                onClick={() =>
                  setTextEditor({ ...textEditor, italic: !textEditor.italic })
                }
              >
                <i>{tx("Italic")}</i>
              </button>
            </div>
            <p className="muted">{tx("Drag the box to move it. Use its corner handle to resize, or double-click to edit.")}</p>
            <div className="actions">
              <button
                type="button"
                className="btn outline"
                onClick={() => setTextEditor(null)}
              >{tx("Cancel")}</button>
              <button
                className="btn primary"
                disabled={locked || !textEditor.text?.trim()}
              >{tx("Save text")}</button>
            </div>
          </form>
        </DashboardDialog>
      )}
    </section>
  );
}
export function PublicBoardPage() {
  usePageText();
  const { key } = useParams(),
    [params] = useSearchParams(),
    presentation = params.get("presentation") === "1";
  return (
    <main
      className={presentation ? "presentation-board" : undefined}
      style={presentation ? undefined : { padding: 12 }}
    >
      <InteractiveBoard
        viewerKey={key}
        token={params.get("t") ?? undefined}
        presentation={presentation}
      />
    </main>
  );
}
